import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Plus, 
  User,
  MagnifyingGlass,
  PencilSimple,
  Envelope,
  Phone,
  Trash,
  Shield,
  House,
  Crown,
  Users as UsersIcon,
  UserCirclePlus,
  CheckCircle,
  XCircle,
  Buildings,
  Export,
  Key,
  CaretUp,
  CaretDown,
  CaretUpDown
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { getSuperAdminData, createUser, updateUser, deleteUser, invalidateCommHubUsersCache, User as ApiUser } from '../lib/api';

// Role hierarchy and permissions - default configs
const DEFAULT_ROLES: Record<string, { level: number; label: string; color: string; icon: React.ElementType }> = {
  superadmin: { level: 100, label: 'Superadmin', color: 'bg-purple-100 text-purple-800', icon: Crown },
  admin: { level: 80, label: 'Admin', color: 'bg-red-100 text-red-800', icon: Shield },
  board_member: { level: 60, label: 'Styrelsemedlem', color: 'bg-amber-100 text-amber-800', icon: UsersIcon },
  auditor: { level: 55, label: 'Revisor', color: 'bg-blue-100 text-blue-800', icon: Shield },
  suppliant: { level: 50, label: 'Suppleant', color: 'bg-indigo-100 text-indigo-800', icon: UsersIcon },
  resident: { level: 20, label: 'Boende', color: 'bg-slate-100 text-slate-800', icon: User },
  subcontractor: { level: 10, label: 'Leverantör', color: 'bg-green-100 text-green-800', icon: House },
  external: { level: 7, label: 'Extern', color: 'bg-sky-100 text-sky-800', icon: User }
};

// Dynamic role colors for unknown roles
const DYNAMIC_ROLE_COLORS = [
  'bg-teal-100 text-teal-800',
  'bg-orange-100 text-orange-800',
  'bg-pink-100 text-pink-800',
  'bg-cyan-100 text-cyan-800',
];

// Function to get role config
const getRoleConfig = (role: string | undefined, allRoles: Record<string, { level: number; label: string; color: string; icon: React.ElementType }>) => {
  if (!role) return DEFAULT_ROLES.resident;
  if (allRoles[role]) return allRoles[role];
  if (DEFAULT_ROLES[role]) return DEFAULT_ROLES[role];
  
  const colorIndex = role.charCodeAt(0) % DYNAMIC_ROLE_COLORS.length;
  return {
    level: 30,
    label: role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' '),
    color: DYNAMIC_ROLE_COLORS[colorIndex],
    icon: User
  };
};

// User type for this page
interface UserData {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  personal_number?: string;
  role?: string;
  organization_id?: string;
  apartment_id?: string;
  is_active?: boolean;
}

interface OrgData {
  id: string;
  name?: string;
}

interface ApartmentData {
  id: string;
  apartment_number?: string;
  organization_id?: string;
  residents?: Array<{ id: string; name?: string }>;
}

type SortField = 'name' | 'role' | 'organization' | 'apartment' | 'status';
type SortDirection = 'asc' | 'desc';

export default function SuperAdminUsersPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserData[]>([]);
  const [organizations, setOrganizations] = useState<OrgData[]>([]);
  const [apartments, setApartments] = useState<ApartmentData[]>([]);
  const [ROLES, setROLES] = useState(DEFAULT_ROLES);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserData | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserData | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    personal_number: '',
    role: 'admin',
    organization_id: '',
    apartment_id: '',
    is_active: true
  });

  const fetchData = useCallback(async () => {
    try {
      const data = await getSuperAdminData();
      const fetchedUsers = data.users || [];
      const fetchedOrgs = data.organizations || [];
      
      // Filter out orphaned users (those without a valid organization)
      const orgIds = new Set(fetchedOrgs.map(o => o.id));
      const validUsers = fetchedUsers.filter((u: UserData) => 
        u.organization_id && orgIds.has(u.organization_id)
      );
      
      setUsers(validUsers);
      setOrganizations(fetchedOrgs);
      setApartments(data.apartments || []);
      
      // Build dynamic roles from valid users
      const dynamicRoles = { ...DEFAULT_ROLES };
      validUsers.forEach((u: UserData) => {
        if (u.role && !dynamicRoles[u.role]) {
          dynamicRoles[u.role] = getRoleConfig(u.role, dynamicRoles);
        }
      });
      setROLES(dynamicRoles);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = !search || 
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.phone?.includes(search);
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    const matchesOrg = orgFilter === 'all' || u.organization_id === orgFilter;
    return matchesSearch && matchesRole && matchesOrg;
  });

  // Helper functions for sorting (must be defined before sortedUsers)
  const getOrgName = (orgId: string | undefined) => {
    if (!orgId) return 'Ingen förening';
    const org = organizations.find(o => o.id === orgId);
    return org?.name || 'Okänd';
  };

  const getApartmentNumber = (aptId: string | undefined, userId?: string) => {
    if (!aptId && !userId) return null;
    
    // Strategy 1: Match by apartment ID (UUID)
    if (aptId) {
      const apt = apartments.find(a => a.id === aptId);
      if (apt?.apartment_number) {
        return apt.apartment_number;
      }
      
      // Check if aptId might already be an apartment number
      const isLikelyApartmentNumber = aptId.length <= 5 && !aptId.includes('-');
      if (isLikelyApartmentNumber) {
        return aptId;
      }
    }
    
    // Strategy 2: Reverse lookup - find apartment that has this user in residents
    if (userId) {
      const aptWithResident = apartments.find(apt => 
        apt.residents?.some(r => r.id === userId)
      );
      if (aptWithResident?.apartment_number) {
        return aptWithResident.apartment_number;
      }
    }
    
    return null;
  };

  // Sort users
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <CaretUpDown size={14} className="text-slate-400" />;
    return sortDirection === 'asc' 
      ? <CaretUp size={14} className="text-primary" weight="bold" />
      : <CaretDown size={14} className="text-primary" weight="bold" />;
  };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortField) {
      case 'name':
        return direction * (a.name || '').localeCompare(b.name || '', 'sv');
      case 'role':
        const roleA = ROLES[a.role as keyof typeof ROLES]?.level || 0;
        const roleB = ROLES[b.role as keyof typeof ROLES]?.level || 0;
        return direction * (roleB - roleA); // Higher level first when ascending
      case 'organization':
        const orgA = getOrgName(a.organization_id);
        const orgB = getOrgName(b.organization_id);
        return direction * orgA.localeCompare(orgB, 'sv');
      case 'apartment':
        const aptA = getApartmentNumber(a.apartment_id, a.id) || '';
        const aptB = getApartmentNumber(b.apartment_id, b.id) || '';
        return direction * aptA.localeCompare(aptB, 'sv', { numeric: true });
      case 'status':
        const statusA = a.is_active !== false ? 1 : 0;
        const statusB = b.is_active !== false ? 1 : 0;
        return direction * (statusB - statusA); // Active first when ascending
      default:
        return 0;
    }
  });

  const handleOpenDialog = (user: UserData | null = null) => {
    if (user) {
      setEditUser(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        personal_number: user.personal_number || '',
        role: user.role || 'admin',
        organization_id: user.organization_id || '',
        apartment_id: user.apartment_id || '',
        is_active: user.is_active !== false
      });
    } else {
      setEditUser(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        personal_number: '',
        role: 'admin',
        organization_id: '',
        apartment_id: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Fyll i namn och e-post');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Ogiltig e-postadress');
      return;
    }
    
    try {
      const userData: Record<string, unknown> = {
        ...formData,
        organization_id: formData.organization_id || null,
        apartment_id: formData.apartment_id || null,
        updated_at: new Date().toISOString()
      };

      if (editUser && editUser.id) {
        await updateUser(editUser.id, userData);
        toast.success('Användare uppdaterad');
      } else {
        userData.id = crypto.randomUUID();
        userData.created_at = new Date().toISOString();
        await createUser(userData);
        toast.success('Användare skapad');
      }
      
      invalidateCommHubUsersCache();
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara');
    }
  };

  const handleDeleteClick = (user: UserData) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete || !userToDelete.id) return;
    
    try {
      await deleteUser(userToDelete.id);
      toast.success('Användare borttagen');
      invalidateCommHubUsersCache();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Kunde inte ta bort användare');
    }
  };

  // Quick role change directly from table
  const handleQuickRoleChange = async (user: UserData, newRole: string) => {
    if (!user.id) return;
    try {
      await updateUser(user.id, { role: newRole as ApiUser['role'] });
      toast.success(`${user.name || user.email} är nu ${ROLES[newRole as keyof typeof ROLES]?.label}`);
      invalidateCommHubUsersCache();
      fetchData();
    } catch (error) {
      console.error('Role change error:', error);
      toast.error('Kunde inte ändra roll');
    }
  };

  const RoleBadge = ({ role }: { role: string | undefined }) => {
    const config = getRoleConfig(role, ROLES);
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} weight="bold" />
        {config.label}
      </span>
    );
  };

  const StatusBadge = ({ isActive }: { isActive: boolean | undefined }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`}>
      {isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
      {isActive ? 'Aktiv' : 'Inaktiv'}
    </span>
  );

  // Group users by organization for stats
  const usersByOrg = organizations.map(org => ({
    ...org,
    userCount: users.filter(u => u.organization_id === org.id).length
  }));

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="superadmin-users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Alla användare
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Hantera användare över alla föreningar
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <Export size={18} />
            Exportera
          </Button>
          <Button onClick={() => handleOpenDialog()} className="btn-press gap-2" data-testid="add-user-button">
            <UserCirclePlus size={18} />
            Ny användare
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card-flat text-center">
          <div className="text-2xl font-bold text-slate-900">{users.length}</div>
          <div className="text-xs text-slate-500">Totalt</div>
        </div>
        <div className="card-flat text-center">
          <div className="text-2xl font-bold text-purple-600">
            {users.filter(u => u.role === 'superadmin').length}
          </div>
          <div className="text-xs text-slate-500">Superadmins</div>
        </div>
        <div className="card-flat text-center">
          <div className="text-2xl font-bold text-red-600">
            {users.filter(u => u.role === 'admin').length}
          </div>
          <div className="text-xs text-slate-500">Admins</div>
        </div>
        <div className="card-flat text-center">
          <div className="text-2xl font-bold text-slate-600">
            {users.filter(u => u.role === 'resident' || u.role === 'board_member').length}
          </div>
          <div className="text-xs text-slate-500">Boende/Styrelse</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-flat">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök namn, e-post, telefon..."
              className="pl-10"
              data-testid="users-search-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[160px]" data-testid="role-filter">
              <SelectValue placeholder="Roll" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla roller</SelectItem>
              {Object.entries(ROLES).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger className="w-[200px]" data-testid="org-filter">
              <SelectValue placeholder="Förening" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla föreningar</SelectItem>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* User List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="card-flat animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-slate-200" />
                <div className="flex-1">
                  <div className="h-4 w-40 bg-slate-200 rounded mb-2" />
                  <div className="h-3 w-56 bg-slate-200 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="card-flat">
          <div className="empty-state py-12">
            <User size={48} className="empty-state-icon" />
            <p className="text-slate-500">Inga användare hittades</p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <button 
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-1 hover:text-primary transition-colors w-full text-left"
                    data-testid="sort-name"
                  >
                    Användare {getSortIcon('name')}
                  </button>
                </th>
                <th>
                  <button 
                    onClick={() => handleSort('role')}
                    className="flex items-center gap-1 hover:text-primary transition-colors w-full text-left"
                    data-testid="sort-role"
                  >
                    Roll {getSortIcon('role')}
                  </button>
                </th>
                <th>
                  <button 
                    onClick={() => handleSort('organization')}
                    className="flex items-center gap-1 hover:text-primary transition-colors w-full text-left"
                    data-testid="sort-organization"
                  >
                    Förening {getSortIcon('organization')}
                  </button>
                </th>
                <th>
                  <button 
                    onClick={() => handleSort('apartment')}
                    className="flex items-center gap-1 hover:text-primary transition-colors w-full text-left"
                    data-testid="sort-apartment"
                  >
                    Lägenhet {getSortIcon('apartment')}
                  </button>
                </th>
                <th>
                  <button 
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-1 hover:text-primary transition-colors w-full text-left"
                    data-testid="sort-status"
                  >
                    Status {getSortIcon('status')}
                  </button>
                </th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user) => (
                <tr key={user.id} data-testid={`user-row-${user.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        ROLES[user.role as keyof typeof ROLES]?.color || 'bg-slate-100'
                      }`}>
                        {(() => {
                          const Icon = ROLES[user.role as keyof typeof ROLES]?.icon || User;
                          return <Icon size={18} weight="bold" />;
                        })()}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{user.name || '-'}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2">
                          <span className="flex items-center gap-1">
                            <Envelope size={12} />
                            {user.email}
                          </span>
                          {user.phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={12} />
                              {user.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {/* Role dropdown - superadmin can change any role */}
                    <Select
                      value={user.role}
                      onValueChange={(newRole) => handleQuickRoleChange(user, newRole)}
                    >
                      <SelectTrigger className="h-7 w-auto gap-1 px-2 border-0 bg-transparent hover:bg-slate-100">
                        <RoleBadge role={user.role} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(ROLES).map(([key, config]) => (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <RoleBadge role={key} />
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <div className="flex items-center gap-1 text-sm">
                      <Buildings size={14} className="text-slate-400" />
                      {getOrgName(user.organization_id)}
                    </div>
                  </td>
                  <td>
                    <div className="text-sm text-slate-600">
                      {getApartmentNumber(user.apartment_id, user.id) ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                          <House size={12} />
                          Lgh {getApartmentNumber(user.apartment_id, user.id)}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </div>
                  </td>
                  <td><StatusBadge isActive={user.is_active !== false} /></td>
                  <td>
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => handleOpenDialog(user)}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md"
                        data-testid={`edit-user-${user.id}`}
                        title="Redigera"
                      >
                        <PencilSimple size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(user)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        data-testid={`delete-user-${user.id}`}
                        title="Ta bort"
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editUser ? <PencilSimple size={20} /> : <UserCirclePlus size={20} />}
              {editUser ? 'Redigera användare' : 'Ny användare'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Namn *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Förnamn Efternamn"
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>E-post *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="namn@example.com"
                  disabled={!!editUser}
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="070-123 45 67"
                  data-testid="user-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Personnummer</Label>
                <Input
                  value={formData.personal_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, personal_number: e.target.value }))}
                  placeholder="YYYYMMDD-XXXX"
                  className="font-mono"
                  data-testid="user-pnr-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Roll</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLES).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Förening</Label>
                <Select
                  value={formData.organization_id || 'none'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, organization_id: value === 'none' ? '' : value, apartment_id: '' }))}
                >
                  <SelectTrigger data-testid="user-org-select">
                    <SelectValue placeholder="Välj förening" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ingen förening</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Lägenhet</Label>
                <Select
                  value={formData.apartment_id || 'none'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, apartment_id: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger data-testid="user-apartment-select">
                    <SelectValue placeholder="Välj lägenhet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ingen lägenhet</SelectItem>
                    {apartments.map((apt) => (
                      <SelectItem key={apt.id} value={apt.id}>Lgh {apt.apartment_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.is_active ? 'active' : 'inactive'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, is_active: value === 'active' }))}
                >
                  <SelectTrigger data-testid="user-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktiv</SelectItem>
                    <SelectItem value="inactive">Inaktiv</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Avbryt
              </Button>
              <Button onClick={handleSave} className="flex-1" data-testid="save-user-button">
                {editUser ? 'Spara ändringar' : 'Skapa användare'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort användare?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort <strong>{userToDelete?.name || userToDelete?.email}</strong>?
              Detta kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
